/**
 * The `<cite source=""/>` output contract (workspace chat).
 *
 * The model cites a session by echoing, verbatim, a session id it received
 * from a tool result in this thread. Both sides of the contract live here:
 * the server validates persisted turns against the thread's read set (the
 * hallucination signal), and the client rewrites tokens for display —
 * valid cites become chips, invalid cites disappear into plain prose.
 */

const CITE_TOKEN_PATTERN = /<cite\s+source="([^"]*)"\s*\/>/g;

/** Prefix for the markdown href a valid cite is rewritten to. */
export const CITE_HREF_PREFIX = "#cite:";

export function extractCiteSourceIds(text: string): string[] {
  const ids: string[] = [];

  for (const match of text.matchAll(CITE_TOKEN_PATTERN)) {
    ids.push(match[1]);
  }

  return ids;
}

export interface CiteValidation {
  validIds: string[];
  rejectedCount: number;
}

/**
 * Split cite tokens by whether the thread actually read the cited source.
 * Rejected ids are counted, never returned — a fabricated "id" can be
 * arbitrary model text and must not travel into logs.
 */
export function validateCites(
  text: string,
  readSessionIds: ReadonlySet<string>
): CiteValidation {
  const validIds: string[] = [];
  let rejectedCount = 0;

  for (const id of extractCiteSourceIds(text)) {
    if (readSessionIds.has(id)) {
      validIds.push(id);
    } else {
      rejectedCount += 1;
    }
  }

  return { validIds, rejectedCount };
}

/**
 * Rewrite cite tokens for markdown display: a valid cite becomes a link
 * (`[label](#cite:<id>)`) the renderer turns into a chip that opens the
 * source; an invalid cite is stripped, leaving the surrounding claim as
 * plain text — the anti-hallucination guard the ticket requires.
 */
export function renderCiteTokens(
  text: string,
  readSessionIds: ReadonlySet<string>,
  labelFor: (sessionId: string) => string
): string {
  return text.replace(CITE_TOKEN_PATTERN, (_match, sourceId: string) => {
    if (!readSessionIds.has(sourceId)) return "";

    const label = labelFor(sourceId).replace(/[[\]]/g, "");

    return `[${label}](${CITE_HREF_PREFIX}${encodeURIComponent(sourceId)})`;
  });
}

/**
 * Remove all cite tokens — clipboard copies of assistant text should carry
 * the prose, not the machine contract (or internal session ids).
 */
export function stripCiteTokens(text: string): string {
  return text.replace(CITE_TOKEN_PATTERN, "").replace(/[ \t]+([.,;:!?])/g, "$1");
}

/** Parse a cite href produced by renderCiteTokens back into a session id. */
export function parseCiteHref(href: string): string | null {
  if (!href.startsWith(CITE_HREF_PREFIX)) return null;

  try {
    return decodeURIComponent(href.slice(CITE_HREF_PREFIX.length));
  } catch {
    return null;
  }
}
